import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  appendChatMessageToCache,
  workspaceChatQueryKeys,
} from '../queries/chats-query'
import type {
  ChatSummary,
  SendWorkspaceChatMessageRequest,
  SendWorkspaceChatMessageResponse,
} from '@/features/chats/types/chat'
import { getPlanLimitNotification } from '@/shared/api/errors'
import { notificationActions } from '@/shared/notifications/notification-store'
import { apiClient } from '@/shared/api/client'
import { trackMessageSent } from '@/shared/analytics'

interface SendWorkspaceChatMessageVariables {
  workspaceId: string
  chatId: string
  payload: SendWorkspaceChatMessageRequest
}

export function useSendWorkspaceChatMessageMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['workspace-chat', 'message', 'send'],
    mutationFn: ({
      workspaceId,
      chatId,
      payload,
    }: SendWorkspaceChatMessageVariables): Promise<SendWorkspaceChatMessageResponse> =>
      apiClient.chats
        .sendChatMessage(workspaceId, chatId, payload)
        .then((message) => ({
          ...message,
          role: 'USER',
        })),
    onSuccess: (message, variables) => {
      appendChatMessageToCache(
        queryClient,
        variables.workspaceId,
        variables.chatId,
        message,
      )

      queryClient.setQueryData<Array<ChatSummary>>(
        workspaceChatQueryKeys.list(variables.workspaceId),
        (current) => {
          if (!current) {
            return current
          }

          const existing = current.find((item) => item.id === variables.chatId)
          if (!existing) {
            return current
          }

          const nextItem: ChatSummary = {
            ...existing,
            updatedAt: message.createdAt,
            lastMessageAt: message.createdAt,
          }

          return [
            nextItem,
            ...current.filter((item) => item.id !== variables.chatId),
          ]
        },
      )

      void queryClient.invalidateQueries({
        queryKey: workspaceChatQueryKeys.list(variables.workspaceId),
      })

      trackMessageSent({ workspace_id: variables.workspaceId })
    },
    onError: async (error) => {
      const limitNotification = await getPlanLimitNotification(error)
      if (limitNotification) {
        notificationActions.info(
          limitNotification.title,
          limitNotification.description,
        )
      }
    },
  })
}
